import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import { ImageCarousel } from "../components/common/ImageCarousel";
import {
  PaymentMethodSelector,
  PaymentMethod,
} from "../components/common/PaymentMethodSelector";
import { usePromotion, getDiscountedPrice } from "../hooks/usePromotion";
import {
  Upload,
  CheckCircle,
  Image as ImageIcon,
  Package,
  Sparkles,
} from "lucide-react";
import { CopyableCode } from "../components/common/CopyableCode";
import styles from "./CustomOrdersPage.module.css";

// Flat fee added when the order is marked as a gift. Kept in sync with the
// backend's GIFT_FEE (src/utils/giftFee.js); the server always computes the
// real charge, this is just for an accurate pre-submit estimate.
const GIFT_FEE = 40;

// Kept in sync with the backend's allow-list (src/middleware/upload.js)
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];

const CUSTOM_ORDERS_GALLERY = [
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (3).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (1).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (5).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.47 PM (3).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (1).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (9).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.43 PM.jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.43 PM (1).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.44 PM.jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.44 PM (1).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.44 PM (2).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM.jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (2).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (4).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (8).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.45 PM (10).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM.jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (2).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (3).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (4).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (5).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (6).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.46 PM (8).jpeg",
  // "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.47 PM.jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.47 PM (1).jpeg",
  "/Custom Orders catalogue/WhatsApp Image 2026-07-19 at 12.07.47 PM (2).jpeg",
];

interface PriceOption {
  size: string;
  price: number;
}

type PricingTable = Record<string, PriceOption[]>;

const PORTRAIT_COUNT_LABELS: Record<string, string> = {
  "1": "1 Person",
  "2": "2 People",
  "3": "3 People",
  "4-6": "4-6 People",
  "7-12": "7-12 People",
};

interface Governorate {
  _id: string;
  name: string;
  shippingCost: number;
}

export function CustomOrdersPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [orderReference, setOrderReference] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingTable>({});
  const [landscapePricing, setLandscapePricing] = useState<PriceOption[]>([]);
  const [othersPricing, setOthersPricing] = useState<PriceOption[]>([]);
  const [governorates, setGovernorates] = useState<Governorate[]>([]);
  const [formData, setFormData] = useState({
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    deliveryAddress: "",
    governorate: "",
    area: "",
    paintingType: "portrait" as "portrait" | "landscape" | "others",
    portraitCount: "",
    canvasSize: "",
    specialInstructions: "",
    isGift: false,
    giftMessage: "",
    paymentMethod: "cod" as PaymentMethod,
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/custom-orders/pricing`,
        );
        const data = await response.json();
        setPricing(data.pricing || {});
        setLandscapePricing(data.landscapePricing || []);
        setOthersPricing(data.othersPricing || []);
      } catch (error) {
        console.error("Error fetching custom order pricing:", error);
      }
    };

    const fetchGovernorates = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/governorates`);
        const data = await response.json();
        setGovernorates(data.governorates || []);
      } catch (error) {
        console.error("Error fetching governorates:", error);
      }
    };

    fetchPricing();
    fetchGovernorates();
  }, []);

  const portraitCounts = Object.keys(pricing);
  const availableSizes: PriceOption[] =
    formData.paintingType === "landscape"
      ? landscapePricing
      : formData.paintingType === "others"
        ? othersPricing
        : formData.portraitCount
          ? pricing[formData.portraitCount] || []
          : [];
  const selectedSizePrice =
    availableSizes.find((s) => s.size === formData.canvasSize)?.price || 0;
  const shippingCost =
    governorates.find((g) => g.name === formData.governorate)?.shippingCost ||
    0;
  const { promotion, isActive } = usePromotion();
  const customDiscountPercent =
    isActive && promotion?.appliesTo.customOrders
      ? promotion.discountPercent
      : 0;
  const discountedSizePrice =
    customDiscountPercent > 0
      ? getDiscountedPrice(selectedSizePrice, customDiscountPercent)
      : selectedSizePrice;
  const giftFee = formData.isGift ? GIFT_FEE : 0;
  const orderTotal = discountedSizePrice + shippingCost + giftFee;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const dotIndex = file.name.lastIndexOf(".");
    const ext = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : "";
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
      setSubmitError(
        "This file type isn't supported. Please upload a JPEG, PNG, WebP, or HEIC image.",
      );
      event.target.value = "";
      return;
    }

    setSubmitError(null);
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validatePhoneNumber = (phone: string) => {
    if (!phone) {
      setPhoneError(null);
      return;
    }
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length > 0 && !digitsOnly.startsWith("0")) {
      setPhoneError("Egyptian phone number must start with 0");
      return;
    }
    if (digitsOnly.length > 11) {
      setPhoneError("Phone number cannot exceed 11 digits");
      return;
    }
    if (digitsOnly.length > 0 && digitsOnly.length < 11) {
      setPhoneError(
        `${11 - digitsOnly.length} more digit${11 - digitsOnly.length === 1 ? "" : "s"} needed`,
      );
      return;
    }
    setPhoneError(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === "guestPhone") {
      validatePhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      // Reset the size choice whenever the group changes, since valid sizes differ per group
      ...(name === "portraitCount" ? { canvasSize: "" } : {}),
      ...(name === "paintingType" ? { canvasSize: "", portraitCount: "" } : {}),
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedFile) {
      setSubmitError("Please upload an image");
      return;
    }

    const digitsOnly = formData.guestPhone.replace(/\D/g, "");
    if (!digitsOnly.startsWith("0") || digitsOnly.length !== 11) {
      setPhoneError(
        !digitsOnly.startsWith("0")
          ? "Egyptian phone number must start with 0"
          : "Egyptian phone number must be exactly 11 digits",
      );
      return;
    }

    if (formData.paintingType === "portrait" && !formData.portraitCount) {
      setSubmitError("Please select how many people are in your portrait");
      return;
    }

    if (!formData.canvasSize) {
      setSubmitError("Please select a canvas size");
      return;
    }

    if (!formData.governorate) {
      setSubmitError("Please select your governorate");
      return;
    }

    if (!formData.deliveryAddress.trim()) {
      setSubmitError("Please enter a delivery address");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const body = new FormData();
      body.append("image", selectedFile);
      body.append("paintingType", formData.paintingType);
      body.append("guestName", formData.guestName);
      body.append("guestEmail", formData.guestEmail);
      body.append("guestPhone", formData.guestPhone);
      body.append("deliveryAddress", formData.deliveryAddress);
      body.append("governorate", formData.governorate);
      body.append("area", formData.area);
      body.append("portraitCount", formData.portraitCount);
      body.append("canvasSize", formData.canvasSize);
      body.append("specialInstructions", formData.specialInstructions);
      body.append("isGift", String(formData.isGift));
      body.append("giftMessage", formData.giftMessage);
      body.append("paymentMethod", formData.paymentMethod);

      const response = await fetch(`${API_BASE_URL}/api/custom-orders/`, {
        method: "POST",
        body,
      });

      const data = await response.json();

      if (!response.ok) {
        // The backend's generic error handler always sets `message` to a
        // fixed fallback string and puts the actual, specific error text in
        // `error` (e.g. "This file type isn't supported...") — prefer that.
        throw new Error(data.error || data.message || "Failed to submit custom order");
      }

      setOrderReference(data.order?.referenceNumber || null);
      setSubmitSuccess(true);
      // The success message replaces the (long) form in place, but the page
      // is still scrolled to wherever the submit button was — bring the
      // confirmation into view instead of leaving the user looking at
      // whatever now sits at their old scroll position.
      requestAnimationFrame(() => {
        successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (error: any) {
      setSubmitError(error.message || "Failed to submit custom order");
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: ImageIcon,
      title: "Your Photo",
      description:
        "Upload any image you want to turn into a paint by numbers kit",
    },
    {
      icon: Package,
      title: "Complete Kit",
      description:
        "Receive a full kit with numbered canvas, paints, and brushes",
    },
    {
      icon: Sparkles,
      title: "Unique Creation",
      description:
        "Create a one-of-a-kind artwork that's personally meaningful",
    },
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Upload Your Image",
      description:
        "Choose a photo from your collection - family photos, pets, landscapes, or any image you love",
    },
    {
      step: "2",
      title: "Select Options",
      description: "Pick your canvas size and any special requirements",
    },
    {
      step: "3",
      title: "We Process",
      description:
        "Our team converts your image into a custom paint by numbers design",
    },
    {
      step: "4",
      title: "Receive Your Kit",
      description:
        "Get your personalized kit delivered with everything you need to start painting",
    },
  ];

  return (
    <div className={styles.customOrdersPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Custom Paint by Numbers</h1>
          <p className={styles.heroSubtitle}>
            Transform your favorite photos into personalized paint by numbers
            kits
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <Icon size={32} />
                  </div>
                  <h3 className={styles.featureTitle}>{feature.title}</h3>
                  <p className={styles.featureDescription}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Order Form Section */}
      <section className={styles.orderFormSection}>
        <div className={styles.container}>
          <div className={styles.formWrapper}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Create Your Custom Order</h2>
              <p className={styles.formSubtitle}>
                Fill out the form below and upload your image to get started
              </p>
            </div>

            <form onSubmit={handleSubmit} className={styles.orderForm}>
              {submitError && (
                <div className={styles.errorMessage}>{submitError}</div>
              )}

              {submitSuccess ? (
                <div className={styles.successMessage} ref={successRef}>
                  <CheckCircle size={48} style={{ color: "#10b981" }} />
                  <h3>Order Submitted!</h3>
                  <p>
                    Your custom order request has been received and is pending
                    review. We'll contact you within 24 hours with a quote and
                    timeline.
                  </p>
                  {orderReference && (
                    <div className={styles.referenceSection}>
                      <p className={styles.referenceLabel}>
                        Your reference number
                      </p>
                      <CopyableCode value={orderReference} />
                      <p className={styles.referenceHint}>
                        Save this — quote it if you need to ask us about your
                        order.
                      </p>
                    </div>
                  )}
                  {formData.paymentMethod !== "cod" && (
                    <p>
                      Don't forget to send your payment screenshot on
                      WhatsApp so we can confirm your order.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {/* Personal Information */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>
                      Personal Information
                    </h3>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="guestName"
                          value={formData.guestName}
                          onChange={handleFormChange}
                          placeholder="Enter your name"
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Email *</label>
                        <input
                          type="email"
                          name="guestEmail"
                          value={formData.guestEmail}
                          onChange={handleFormChange}
                          placeholder="Enter your email"
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          name="guestPhone"
                          value={formData.guestPhone}
                          onChange={handleFormChange}
                          placeholder="01XXXXXXXXX (11 digits)"
                          required
                          style={{
                            borderColor: phoneError ? "#ef4444" : undefined,
                            borderWidth: phoneError ? "2px" : undefined,
                          }}
                        />
                        {phoneError && (
                          <span
                            style={{
                              color: "#ef4444",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                              display: "block",
                            }}
                          >
                            {phoneError}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Governorate *</label>
                        <select
                          name="governorate"
                          value={formData.governorate}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">Select your governorate</option>
                          {governorates.map((gov) => (
                            <option key={gov._id} value={gov.name}>
                              {gov.name} — EGP {gov.shippingCost}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.formGroup}>
                        <label>Area (Optional)</label>
                        <input
                          type="text"
                          name="area"
                          value={formData.area}
                          onChange={handleFormChange}
                          placeholder="e.g., Nasr City, Maadi..."
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Delivery Address *</label>
                        <input
                          type="text"
                          name="deliveryAddress"
                          value={formData.deliveryAddress}
                          onChange={handleFormChange}
                          placeholder="Building, street, and any landmarks"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Upload Your Image</h3>
                    <div className={styles.uploadArea}>
                      <input
                        type="file"
                        id="imageUpload"
                        // No `accept` filter: iOS Safari's photo picker
                        // tries to auto-convert HEIC photos when `accept`
                        // includes "image/*", and that conversion step
                        // itself can throw ("The string did not match the
                        // expected pattern") for photos still stored in
                        // iCloud in high-efficiency format. We validate the
                        // file type ourselves in handleFileChange instead.
                        onChange={handleFileChange}
                        className={styles.fileInput}
                      />
                      <label
                        htmlFor="imageUpload"
                        className={styles.uploadLabel}
                      >
                        {previewUrl ? (
                          <div className={styles.previewContainer}>
                            <img
                              src={previewUrl}
                              alt="Preview"
                              className={styles.previewImage}
                            />
                            <div className={styles.changeImageOverlay}>
                              <Upload size={32} />
                              <span>Change Image</span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.uploadPlaceholder}>
                            <Upload size={48} />
                            <span className={styles.uploadText}>
                              Click to upload your image
                            </span>
                            <span className={styles.uploadHint}>
                              PNG, JPG, JPEG, HEIC (Max 10MB)
                            </span>
                          </div>
                        )}
                      </label>
                    </div>
                    <p className={styles.imageNote}>
                      <strong>Tip:</strong> High-resolution images (at least
                      1000x1000px) work best for detailed results
                    </p>
                  </div>

                  {/* Painting Type */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Painting Type</h3>
                    <div className={styles.radioGrid}>
                      <label className={styles.radioCard}>
                        <input
                          type="radio"
                          name="paintingType"
                          value="portrait"
                          checked={formData.paintingType === "portrait"}
                          onChange={handleFormChange}
                        />
                        <div className={styles.radioContent}>
                          <span className={styles.radioLabel}>Portrait</span>
                          <span className={styles.radioSubLabel}>
                            Photo-based, people & pets
                          </span>
                        </div>
                      </label>
                      <label className={styles.radioCard}>
                        <input
                          type="radio"
                          name="paintingType"
                          value="landscape"
                          checked={formData.paintingType === "landscape"}
                          onChange={handleFormChange}
                        />
                        <div className={styles.radioContent}>
                          <span className={styles.radioLabel}>Landscape</span>
                          <span className={styles.radioSubLabel}>
                            Nature & landscapes
                          </span>
                        </div>
                      </label>
                      <label className={styles.radioCard}>
                        <input
                          type="radio"
                          name="paintingType"
                          value="others"
                          checked={formData.paintingType === "others"}
                          onChange={handleFormChange}
                        />
                        <div className={styles.radioContent}>
                          <span className={styles.radioLabel}>Others</span>
                          <span className={styles.radioSubLabel}>
                            Other designs (Animals, objects, florals,
                            illustrations, scenes & more)
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Number of People — portrait only */}
                  {formData.paintingType === "portrait" && (
                    <div className={styles.formSection}>
                      <h3 className={styles.sectionTitle}>
                        How Many People Are In Your Portrait?
                      </h3>
                      <div className={styles.radioGrid}>
                        {portraitCounts.map((count) => (
                          <label key={count} className={styles.radioCard}>
                            <input
                              type="radio"
                              name="portraitCount"
                              value={count}
                              checked={formData.portraitCount === count}
                              onChange={handleFormChange}
                              required
                            />
                            <div className={styles.radioContent}>
                              <span className={styles.radioLabel}>
                                {PORTRAIT_COUNT_LABELS[count] || count}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Canvas Size */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Canvas Size</h3>
                    {formData.paintingType === "landscape" ||
                    formData.paintingType === "others" ||
                    formData.portraitCount ? (
                      <div className={styles.radioGrid}>
                        {availableSizes.map((size) => (
                          <label key={size.size} className={styles.radioCard}>
                            <input
                              type="radio"
                              name="canvasSize"
                              value={size.size}
                              checked={formData.canvasSize === size.size}
                              onChange={handleFormChange}
                              required
                            />
                            <div className={styles.radioContent}>
                              <span className={styles.radioLabel}>
                                {size.size} cm
                              </span>
                              {customDiscountPercent > 0 ? (
                                <span className={styles.radioPrice}>
                                  <span className={styles.radioOriginalPrice}>
                                    EGP {size.price}
                                  </span>{" "}
                                  <span className={styles.radioDiscountedPrice}>
                                    EGP{" "}
                                    {getDiscountedPrice(
                                      size.price,
                                      customDiscountPercent,
                                    )}
                                  </span>
                                </span>
                              ) : (
                                <span className={styles.radioPrice}>
                                  EGP {size.price}
                                </span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.imageNote}>
                        Select how many people are in your portrait first to see
                        available sizes and pricing.
                      </p>
                    )}
                  </div>

                  {/* Special Instructions */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>
                      Special Instructions (Optional)
                    </h3>
                    <div className={styles.formGroup}>
                      <textarea
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={handleFormChange}
                        rows={4}
                        placeholder="Any special requests or notes about your custom order?"
                        className={styles.textarea}
                      ></textarea>
                    </div>
                  </div>

                  {/* Gift Option */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Gift Option</h3>
                    <div className={styles.formGroup}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          name="isGift"
                          checked={formData.isGift}
                          onChange={handleFormChange}
                        />
                        This order is a gift{" "}
                        <span style={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: 400 }}>
                          (+EGP {GIFT_FEE})
                        </span>
                      </label>
                    </div>
                    {formData.isGift && (
                      <div className={styles.formGroup}>
                        <label>Gift Message (Optional)</label>
                        <textarea
                          name="giftMessage"
                          value={formData.giftMessage}
                          onChange={handleFormChange}
                          rows={3}
                          placeholder="Enter a message for the recipient"
                          className={styles.textarea}
                        ></textarea>
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Payment Method</h3>
                    <PaymentMethodSelector
                      value={formData.paymentMethod}
                      onChange={(paymentMethod) =>
                        setFormData((prev) => ({ ...prev, paymentMethod }))
                      }
                    />
                  </div>

                  {/* Submit Button */}
                  <div className={styles.submitSection}>
                    {formData.canvasSize && formData.governorate && (
                      <div className={styles.orderTotalPreview}>
                        <span>
                          Canvas:{" "}
                          {customDiscountPercent > 0 ? (
                            <>
                              <span className={styles.radioOriginalPrice}>
                                EGP {selectedSizePrice}
                              </span>{" "}
                              <span className={styles.radioDiscountedPrice}>
                                EGP {discountedSizePrice}
                              </span>
                            </>
                          ) : (
                            <>EGP {selectedSizePrice}</>
                          )}
                        </span>
                        <span>Shipping: EGP {shippingCost}</span>
                        {formData.isGift && <span>Gift Fee: EGP {giftFee}</span>}
                        <strong>Total: EGP {orderTotal}</strong>
                      </div>
                    )}
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting}
                    >
                      {submitting
                        ? "Submitting..."
                        : "Submit Custom Order Request"}
                    </Button>
                    <p className={styles.submitNote}>
                      We'll review your order and contact you within 24 hours
                      with a quote and timeline
                    </p>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Catalogue Carousel */}
      <ImageCarousel
        images={CUSTOM_ORDERS_GALLERY}
        title="Our Custom Orders Catalogue"
        subtitle="Browse through paintings we've already created — yours could be next"
      />

      {/* Under catalogue image */}
      <div className={styles.underCatalogueWrap}>
        <img
          src="/under catalogue.jpeg"
          alt=""
          className={styles.underCatalogueImg}
        />
      </div>

      {/* Section 1: Story / About */}
      <section className={styles.storySection}>
        <div className={styles.infoContainer}>
          <h2 className={styles.infoSectionTitle}>
            Custom Paint-by-Number Kits
          </h2>
          <h3 className={styles.storySectionSubheading}>
            Turn Your Story Into Art
          </h3>
          <p className={styles.storyText}>
            Every photograph holds a memory, a place, or a feeling.
          </p>
          <p className={styles.storyText}>
            Our Custom Paint-by-Number Kits transform your favorite photos into
            a relaxing painting experience, turning meaningful moments into art
            you create yourself. Whether it's someone you love, a special place,
            a family portrait, your pet, or a celebration, each kit is
            thoughtfully designed just for you.
          </p>
          <p className={styles.storyText}>
            At ArtsyIcile, we believe art begins with life itself. Painting one
            brushstroke at a time offers a calming, mindful experience while
            helping you reconnect with the memories behind every image—turning
            your story into art.
          </p>
          <h3 className={styles.storySectionSubheading}>A Gift That Lasts</h3>
          <p className={styles.storyText}>
            Custom Paint-by-Number Kits make thoughtful gifts for birthdays,
            anniversaries, weddings, new homes, graduations, memorials, or
            simply to celebrate the people and places that matter most.
          </p>
          <p className={styles.storyText}>
            Because the most meaningful artwork is often the one connected to
            your own story.
          </p>
        </div>
      </section>

      {/* Section 2: What's Included + How to Use Your Kit */}
      <section className={styles.kitInfoSection}>
        <div className={styles.infoContainer}>
          <div className={styles.kitInfoGrid}>
            <div className={styles.kitInfoBlock}>
              <h2 className={styles.infoSectionTitle}>What's Included</h2>
              <p className={styles.kitInfoSubtitle}>
                Every Custom Paint-by-Number Kit includes:
              </p>
              <ul className={styles.includedList}>
                <li>Premium framed cotton canvas</li>
                <li>Numbered acrylic paint set</li>
                <li>Transparent reusable paint palettes</li>
                <li>Fine-detail paint brushes</li>
                <li>Printed reference guide</li>
              </ul>
            </div>
            <div className={styles.kitInfoBlock}>
              <h2 className={styles.infoSectionTitle}>How to Use Your Kit</h2>
              <p className={styles.kitInfoSubtitle}>
                Your kit is designed to be simple and enjoyable, even if you've
                never painted before.
              </p>
              <ol className={styles.stepsList}>
                <li>
                  Open your kit and place the framed canvas on a flat surface.
                </li>
                <li>
                  Inside the box, you'll find numbered acrylic paint tubes and
                  transparent empty palettes.
                </li>
                <li>
                  Squeeze a small amount of each paint color into its matching
                  numbered palette compartment.
                </li>
                <li>Paint the matching numbered sections on the canvas.</li>
                <li>
                  Whenever you run out of a color in the palette, simply refill
                  it from the corresponding paint tube.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Helpful Tips */}
      <section className={styles.infoSection}>
        <div className={styles.infoContainer}>
          <h2 className={styles.infoSectionTitle}>Helpful Tips</h2>
          <ul className={styles.tipsList}>
            <li>
              Keep all paint tubes tightly closed whenever you're not using
              them. Acrylic paint dries quickly when exposed to air.
            </li>
            <li>
              Do not add water directly to the paint tubes or the paint itself.
            </li>
            <li>
              Rinse your brushes in a cup of clean water between colors, then
              gently dry them before dipping into the next paint.
            </li>
            <li>
              Start with lighter colors or work from the top of the canvas
              downward if that feels more comfortable.
            </li>
            <li>
              Take your time and enjoy the process—there's no right speed when
              creating art.
            </li>
          </ul>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.howItWorksSection}>
        <div className={styles.container}>
          <h2 className={styles.howItWorksTitle}>How It Works</h2>
          <div className={styles.stepsGrid}>
            {howItWorks.map((step, index) => (
              <div key={index} className={styles.stepCard}>
                <div className={styles.stepNumber}>{step.step}</div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>
          <div className={styles.faqGrid}>
            <div className={styles.faqCard}>
              <h3>How long does it take?</h3>
              <p>
                Custom orders typically take 12-15 days to process and prepare
                for delivery.
              </p>
            </div>
            <div className={styles.faqCard}>
              <h3>What images work best?</h3>
              <p>
                Clear, well-lit photos with good contrast work best. Avoid
                blurry or low-resolution images.
              </p>
            </div>
            <div className={styles.faqCard}>
              <h3>Can I make changes?</h3>
              <p>
                Yes! We'll send you a preview before production. You can request
                adjustments at that time.
              </p>
            </div>
            <div className={styles.faqCard}>
              <h3>What's included in the kit?</h3>
              <p>
                Your custom canvas, numbered acrylic paints, brushes, and
                detailed instructions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
